/**
 * r16q5: chat pictures for a list of conversations. A conversation with `picAt` has one on our server; they're fetched in one request (the
 * rate limit counts requests) and kept in memory for this page's life, keyed by id and `picAt`, so a poll of the list costs nothing and a new
 * picture replaces the old. A failure just leaves the initial in its circle; it's tried again when the list of what's missing changes.
 */
import { useEffect, useMemo, useState } from 'react';
import { chatsApi } from './chatsApi';

const CACHE = new Map();
const MAX_KEPT = 1000;
const MAX_PER_REQUEST = 50;

const keyOf = (t) => `${t.id}|${t.picAt}`;

/** Pure: the ids still needed, at most one request's worth. */
export function picsNeeded(threads, cache = CACHE) {
  const out = [];
  for (const t of threads || []) {
    if (t && t.picAt && !cache.has(keyOf(t)) && out.length < MAX_PER_REQUEST) out.push(t.id);
  }
  return out;
}

/** Pure: put an answer in the cache; returns how many were added. */
export function picsStore(threads, answer, cache = CACHE) {
  const pics = answer && answer.pics && typeof answer.pics === 'object' ? answer.pics : {};
  let n = 0;
  for (const t of threads || []) {
    const b64 = t && pics[t.id];
    if (typeof b64 === 'string' && b64) {
      if (cache.size >= MAX_KEPT) cache.delete(cache.keys().next().value);
      cache.set(keyOf(t), `data:image/jpeg;base64,${b64}`);
      n += 1;
    }
  }
  return n;
}

export function useChatPics(threads) {
  const [, bump] = useState(0);
  const need = useMemo(() => picsNeeded(threads), [threads]);
  const needKey = need.join(',');
  useEffect(() => {
    if (!needKey) return undefined;
    let gone = false;
    const asked = threads;
    chatsApi.pics(needKey.split(',')).then((r) => {
      if (picsStore(asked, r && r.data) > 0 && !gone) bump((x) => x + 1);
    }).catch(() => {});
    return () => { gone = true; };
    // only when the set of missing pictures changes: a poll of the same list asks nothing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needKey]);
  return (t) => (t && t.picAt ? CACHE.get(keyOf(t)) || null : null);
}
